import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Factory,
  Calculator,
  Crosshair,
  Palette,
} from "lucide-react";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import BudgetEstimator from "../forms/BudgetEstimator";
import budgetCalcVideo from "../../assets/videos/budget-calc.mp4";
import designAnimatedVideo from "../../assets/videos/IRAVATHAM-DESIGN-ANIMATED.mp4";
import factoryImage from "../../assets/images/before-after.jpg";

const bentoItems = [
  {
    id: "factory",
    title: "Your Space, Our Craft",
    description:
      "We own our factory because we care about quality. Every piece is crafted with attention to detail, ensuring your home gets the precision it deserves.",
    image: factoryImage,
    link: "/factory",
    linkText: "See how we work",
    size: "large",
    icon: Factory,
  },
  {
    id: "estimator",
    title: "Start Your Journey",
    description:
      "Not sure where to begin? Our quick estimator helps you understand what's possible for your space and budget. No commitment, just clarity.",
    isEstimator: true,
    size: "medium",
    icon: Calculator,
  },
  {
    id: "precision",
    title: "Built to Last",
    description:
      "We know you want interiors that stand the test of time. That's why we use precision manufacturing and quality materials that last for years.",
    image:
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80",
    size: "small",
    icon: Crosshair,
  },
  {
    id: "designs",
    title: "Designs That Reflect You",
    description:
      "Your home should feel like you. Our designers work with you to create spaces that match your style, from rental-friendly solutions to luxury custom designs.",
    video: designAnimatedVideo,
    size: "small",
    icon: Palette,
  },
];

export default function BentoGrid() {
  const [showEstimator, setShowEstimator] = useState(false);

  return (
    <section className="section-spacing bg-background-main">
      <div className="container-custom section-padding">
        <SectionHeading
          label="How We Help"
          title="Solutions designed around your needs."
          align="center"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr">
          {bentoItems.map((item, index) => (
            <AnimatedSection
              key={item.id}
              delay={index * 100}
              className={`${
                item.size === "large"
                  ? "md:col-span-2 lg:col-span-2 lg:row-span-2"
                  : item.size === "medium"
                    ? "md:col-span-2 lg:col-span-2"
                    : "lg:col-span-1"
              }`}
            >
              {item.isEstimator ? (
                <div className="h-full relative rounded-3xl overflow-hidden p-6 md:p-8 flex flex-col justify-between text-white min-h-[280px] md:min-h-[300px]">
                  {/* Video Background */}
                  <div className="absolute inset-0 z-0">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={budgetCalcVideo} type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-primary via-brand-primary/30 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                      <item.icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-heading text-2xl mb-3 text-white">
                      {item.title}
                    </h3>
                    <p className="text-white/80">{item.description}</p>
                  </div>
                  <button
                    onClick={() => setShowEstimator(true)}
                    className="mt-6 relative z-10 inline-flex items-center gap-2 text-white font-medium hover:gap-3 transition-all"
                  >
                    Launch Budget Estimator
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div
                  className={`group relative h-full rounded-3xl overflow-hidden ${
                    item.size === "large"
                      ? "min-h-[400px] md:min-h-[500px]"
                      : "min-h-[280px] md:min-h-[300px]"
                  }`}
                >
                  {/* Background Image or Video */}
                  <div className="absolute inset-0">
                    {item.video ? (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      >
                        <source src={item.video} type="video/mp4" />
                      </video>
                    ) : (
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-primary via-brand-primary/30 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="relative h-full p-8 flex flex-col justify-end text-white">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-2xl mb-2 text-white">
                      {item.title}
                    </h3>
                    <p className="text-white/80 mb-4">{item.description}</p>
                    {item.link && (
                      <Link
                        to={item.link}
                        className="inline-flex items-center gap-2 text-brand-accent font-medium hover:gap-3 transition-all"
                      >
                        {item.linkText}
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </AnimatedSection>
          ))}
        </div>
      </div>

      {/* Budget Estimator Modal */}
      {showEstimator && (
        <BudgetEstimator onClose={() => setShowEstimator(false)} />
      )}
    </section>
  );
}
